import { useEffect, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Phone, Menu, X } from "lucide-react";
import { BRAND } from "../data/site";

const navItems = [
  { to: "/", label: "Home" },
  { to: "/services", label: "Services" },
  { to: "/gallery", label: "Gallery" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

export const Header = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white/85 backdrop-blur-xl border-b border-stone-200/70 backdrop-saturate-150"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
        <div className="flex items-center justify-between h-20">
          <Link to="/" data-testid="header-logo" className="flex items-baseline gap-2 group">
            <span className="font-display text-[15px] sm:text-base tracking-[0.22em] font-semibold text-stone-900">
              MORGAN
            </span>
            <span className="hidden sm:inline font-display text-[15px] sm:text-base tracking-[0.22em] text-stone-500 group-hover:text-[#9B4819] transition-colors">
              CONSTRUCTION
            </span>
          </Link>

          <nav className="hidden lg:flex items-center gap-10">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                data-testid={`nav-${item.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `text-sm tracking-wide transition-colors ${
                    isActive
                      ? "text-[#9B4819] font-semibold"
                      : "text-stone-700 hover:text-stone-900"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-3">
            <a
              href={BRAND.phoneLink}
              data-testid="header-phone-cta"
              className="inline-flex items-center gap-2 bg-[#9B4819] hover:bg-[#7A3813] text-white px-5 py-3 text-sm tracking-wide font-medium transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_12px_24px_-8px_rgba(155,72,25,0.5)]"
            >
              <Phone className="w-4 h-4" strokeWidth={2.25} />
              {BRAND.phoneDisplay}
            </a>
          </div>

          <button
            className="lg:hidden p-2 text-stone-900"
            onClick={() => setOpen(!open)}
            aria-label="Toggle menu"
            data-testid="mobile-menu-toggle"
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {open && (
        <div
          data-testid="mobile-menu"
          className="lg:hidden bg-white border-t border-stone-200"
        >
          <div className="px-6 py-6 flex flex-col gap-5">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                onClick={() => setOpen(false)}
                data-testid={`mobile-nav-${item.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `text-base tracking-wide ${
                    isActive ? "text-[#9B4819] font-semibold" : "text-stone-800"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
            <a
              href={BRAND.phoneLink}
              data-testid="mobile-phone-cta"
              className="inline-flex items-center gap-2 bg-[#9B4819] text-white px-5 py-3 text-sm font-medium w-fit"
            >
              <Phone className="w-4 h-4" />
              {BRAND.phoneDisplay}
            </a>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
